// services/country-storage.service.ts
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { Country } from '../interfaces/doctor.interface';
import { ThemeService } from './theme.service';
//import { Country } from './country.service';

@Injectable({
  providedIn: 'root',
})
export class CountryStorageService {
  private readonly STORAGE_KEY = 'defaultCountry';
  // Removed default country code - no fallback to SN

  // BehaviorSubject pour émettre les changements de pays
  private countrySubject = new BehaviorSubject<Country | null>(
    this.getStoredCountry()
  );

  constructor(
    private themeService: ThemeService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Initialiser avec le pays stocké
    this.initializeCountry();
  }

  /**
   * Vérifie si on est sur le sous-domaine Orange
   * @returns true si on est sur orange.passeportmedical.app (prod) ou preprod-orange.passeportmedical.app (preprod)
   */
  private isOrangeSubdomain(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      // Pour les tests : vérifier le paramètre URL ?testOrange=true
      // const urlParams = new URLSearchParams(window.location.search);
      // if (urlParams.get('testOrange') === 'true') {
      //   return true;
      // }

      const hostname = window.location.hostname;
      const subdomain = hostname.split('.')[0].toLowerCase();
      // Détecter orange.passeportmedical.app (prod) et preprod-orange.passeportmedical.app (preprod)
      return subdomain === 'orange' || subdomain === 'preprod-orange';
    } catch (error) {
      console.error('Error detecting Orange subdomain:', error);
      return false;
    }
  }

  /**
   * Vérifie si on est sur le domaine FAJMA
   * @returns true si on est sur patient.fajma.com (nouveau) ou fajma.passeportmedical.app / preprod-fajma.passeportmedical.app (ancien)
   */
  private isFajmaSubdomain(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const hostname = window.location.hostname.toLowerCase();
      if (hostname === 'patient.fajma.com' || hostname.endsWith('.patient.fajma.com')) {
        return true;
      }
      const subdomain = hostname.split('.')[0];
      return subdomain === 'fajma' || subdomain === 'preprod-fajma';
    } catch (error) {
      console.error('Error detecting FAJMA subdomain:', error);
      return false;
    }
  }

  /**
   * Vérifie si on est sur un sous-domaine qui verrouille le pays (Orange ou FAJMA)
   * @returns true si on est sur un sous-domaine qui verrouille le pays
   */
  private isLockedSubdomain(): boolean {
    return this.isOrangeSubdomain() || this.isFajmaSubdomain();
  }

  /**
   * Observable pour s'abonner aux changements de pays
   */
  get country$(): Observable<Country | null> {
    return this.countrySubject.asObservable();
  }

  /**
   * Obtenir le pays actuel (valeur synchrone)
   */
  get currentCountry(): Country | null {
    return this.countrySubject.value;
  }

  /**
   * Obtenir le code du pays actuel
   */
  get currentCountryCode(): string {
    return this.currentCountry?.code?.toLowerCase() || '';
  }

  /**
   * Définir le pays sélectionné et notifier les abonnés
   */
  setCountry(country: Country): void {
    // Si on est sur un sous-domaine verrouillé (Orange ou FAJMA), empêcher le changement de pays
    if (this.isLockedSubdomain()) {
      // console.log(
      //   'Changement de pays désactivé sur le sous-domaine. Pays verrouillé sur Sénégal.'
      // );
      // Forcer le Sénégal si ce n'est pas déjà le cas
      const currentCountry = this.countrySubject.value;
      if (!currentCountry || currentCountry.code !== 'SN') {
        this.forceSenegalCountry();
      }
      return;
    }

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(country));
      }

      // Émettre le nouveau pays vers tous les abonnés
      this.countrySubject.next(country);

      // Changer le thème en fonction du pays sélectionné
      if (country?.code) {
        this.themeService.changeThemeByCountry(country.code, true);
      }

      // console.log('Country updated:', country.code);
    } catch (error) {
      console.error('Error saving country to localStorage:', error);
    }
  }

  /**
   * Force le pays Sénégal (utilisé pour les sous-domaines Orange et FAJMA)
   */
  private forceSenegalCountry(): void {
    // Créer un objet Country pour le Sénégal
    const senegalCountry: Country = {
      id: 1, // ID par défaut, sera remplacé par le vrai pays depuis l'API
      code: 'SN',
      name: 'Sénégal',
      flag: '/flags/sn.svg', // Chemin par défaut, sera remplacé par le vrai flag
    };

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(senegalCountry));
      }

      // Émettre le pays Sénégal vers tous les abonnés
      this.countrySubject.next(senegalCountry);

      // Ne pas changer le thème car il est verrouillé par le sous-domaine
      // console.log('Pays forcé sur Sénégal pour le sous-domaine Orange');
    } catch (error) {
      console.error('Error forcing Senegal country:', error);
    }
  }

  /**
   * Définir le pays par son code
   */
  setCountryByCode(code: string, countries: Country[]): boolean {
    const country = countries.find(
      (c) => c.code?.toLowerCase() === code.toLowerCase()
    );

    if (country) {
      this.setCountry(country);
      return true;
    }

    return false;
  }

  /**
   * Récupérer le pays stocké depuis localStorage
   */
  private getStoredCountry(): Country | null {
    try {
      if (typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      }
    } catch (error) {
      console.error('Error reading country from localStorage:', error);
    }

    return null;
  }

  /**
   * Initialiser le service avec le pays par défaut si nécessaire
   */
  private initializeCountry(): void {
    // Si on est sur un sous-domaine verrouillé (Orange ou FAJMA), forcer le Sénégal
    if (this.isLockedSubdomain()) {
      const storedCountry = this.getStoredCountry();
      // Si le pays stocké n'est pas le Sénégal, le forcer
      if (!storedCountry || storedCountry.code !== 'SN') {
        this.forceSenegalCountry();
      } else {
        // S'assurer que le pays est bien émis
        this.countrySubject.next(storedCountry);
      }
      return;
    }

    const storedCountry = this.getStoredCountry();
    if (storedCountry) {
      this.countrySubject.next(storedCountry);
    }
  }

  /**
   * Vérifie si le changement de pays est désactivé (sous-domaines Orange ou FAJMA)
   * @returns true si le changement de pays est désactivé
   */
  isCountryChangeDisabled(): boolean {
    return this.isLockedSubdomain();
  }

  /**
   * Met à jour le pays Sénégal avec les vraies données depuis l'API
   * @param countries - Liste des pays chargés depuis l'API
   */
  updateSenegalFromCountries(countries: Country[]): void {
    if (!this.isLockedSubdomain()) {
      return;
    }

    const senegal = countries.find((c) => c.code?.toUpperCase() === 'SN');

    if (senegal) {
      // Mettre à jour avec les vraies données du Sénégal
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(senegal));
        }
        this.countrySubject.next(senegal);
        // console.log("Pays Sénégal mis à jour avec les données de l'API");
      } catch (error) {
        console.error('Error updating Senegal country:', error);
      }
    }
  }

  /**
   * Obtenir le code du pays par défaut
   */
  getDefaultCountryCode(): string {
    return this.currentCountryCode;
  }

  /**
   * Vider le stockage et réinitialiser
   */
  clear(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(this.STORAGE_KEY);
      }
      this.countrySubject.next(null);
    } catch (error) {
      console.error('Error clearing country storage:', error);
    }
  }

  /**
   * Vérifier si un code pays est valide
   */
  isValidCountryCode(code: string, countries: Country[]): boolean {
    return countries.some(
      (country) => country.code?.toLowerCase() === code.toLowerCase()
    );
  }
}
